import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  AlgoliaKeywordSearch,
  AlgoliaLocationSearch,
} from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { createResourceLocatorString } from '../../../../util/routes';
import moment from 'moment';
import { START_DATE } from '../../../../util/dates';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import { DateRangeInput } from '../../../../components/FieldDateRangeInput/FieldDateRangeInput';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import AlgoliaFilters from '../FilterComponent/AlgoliaFilters';
import css from './AlgoliaSearchSection.module.css';
import { useLocationBasedPlaceholder } from '../../../../hooks/useLocationBasedPlaceholder';
import { useHasUserInteracted } from '../../../../hooks/useHasUserInteracted';
import { saveRentalDefaults, pickRentalDefaultsFromValues } from '../../../../util/rentalDefaults';
import { loadAlgoliaInsights } from '../../../../analytics/handlers';
import { getAlgoliaIndexNameForLang } from '../../../../util/urlHelpers';
import { SUPPORTED_LANGUAGES } from '../../../../util/constants';


const MODAL_BREAKPOINT = 768;
const isWindowDefined = typeof window !== 'undefined';
const isDesktopLayout = isWindowDefined && window.innerWidth > MODAL_BREAKPOINT;


const filters = [{ attribute: 'publicData.category', label: 'Category' }];

const moreFilters = [
  { attribute: 'publicData.access_to_rentals', label: 'Access' },
  { attribute: 'publicData.Condition_of_the_item', label: 'Condition' },
  {
    attribute: 'publicData.Trust_and_verification',
    label: 'Trust',
  },
  { attribute: 'publicData.cancelation_policy', label: 'Cancellation' },
  { attribute: 'publicData.reason_for_listing', label: 'Reason' },
  { attribute: 'publicData.security_deposit', label: 'Deposit' },
];

const formatFacetValue = (value) => {
  const str = String(value);
  const needsQuotes = /\\s|:|\"/.test(str);
  const escaped = str.replace(/\"/g, '\\"');
  return needsQuotes ? `\"${escaped}\"` : escaped;
};

const buildFilterPayload = (selectedFilters) => {
  const filtersPayload = [];

  Object.entries(selectedFilters || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && `${value}`.length > 0) {
      filtersPayload.push(`publicData.${key}:${formatFacetValue(value)}`);
    }
  });

  return filtersPayload;
};

/**
 * Returns a new object without the specified properties.
 *
 * @param {Object} obj - The original object.
 * @param {string[]} keysToOmit - Array of property names to omit.
 * @returns {Object} A new object without the specified properties.
 */
function omitProperties(obj, keysToOmit) {
  // Create a new object excluding the properties in keysToOmit
  return Object.keys(obj).reduce((result, key) => {
    if (!keysToOmit.includes(key)) {
      result[key] = obj[key];
    }
    return result;
  }, {});
}

const getInitialValue = (attribute, params, options = []) => {
  const exactAttr = !!attribute ? attribute : null;
  // If the attribute is price.amount, return the price range
  if (exactAttr === 'price.amount' && params['priceRange']?.split(',').length > 1) {
    const min = +params['priceRange'].split(',')[0] / 100;
    const max = +params['priceRange'].split(',')[1] / 100;
    return [min, max];
  }
  const value =
    exactAttr && typeof params == 'object' ? params[exactAttr] : null;
  const option = options.find(o => o.value == value);
  return option;
};

/**
 * Truncates text to specified length and adds ellipsis if longer
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
const truncateText = (text, maxLength = 20) => {
  if (text == null || text === '') {
    return text;
  }
  const str = String(text);
  if (str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + '...';
};

const SearchSection = props => {
  const {
    currentUserLocationInfo,
    routesConfiguration,
    currentUser,
    history,
    currentUserCurrency,
    exchangeRates,
    config,
    intl,
    onBoundsChange,
    queryParams,
    selectedBounds,
    onActivateListing,
    algoliaListings,
    algoliaStores,
    algoliaQueryInProgress,
    facets,
    facetsError,
    facetsInProgress,
    showMap,
    neighborhood,
    hasLocationAvailable,
    defaultSort,
    locale,
    showOnlySearchInputs = false,
    panelOffset = null,
    isDraggingPanel = false,
    searchInputsWrapperRef = null,
    isMapExploring = false,
    onLocationSelection,
    dateRange = {},
    onDateRangeChange,
  } = props;

  const selectedMoreFilters = Object.keys(facets).reduce((acc, key) => {
    let value = getInitialValue(key, queryParams, facets[key]) || null;
    if (value) acc[key] = value;
    return acc;
  }, {});

  // Compute translated sort options from config for the mobile filters modal
  const sortOptions = (config?.search?.sortConfig?.options || []).map(option => {
    const label = option.labelTranslationKeyLong
      ? intl.formatMessage({ id: option.labelTranslationKeyLong })
      : option.labelTranslationKey
        ? intl.formatMessage({ id: option.labelTranslationKey })
        : (option.label || option.key);
    return { key: option.key, label };
  });

  const filterContainerRef = useRef(null);
  const stickyTriggerRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);
  const [focusedDateInput, setFocusedDateInput] = useState(null);
  const [forceShowDateRow, setForceShowDateRow] = useState(false);
  const hasUserInteractedRef = useHasUserInteracted();

  // Expand the date row in-place when triggered by "Change Dates" in the rates sheet,
  // even if it's currently collapsed (sticky + already filled). Mirrors DesktopSearchBar.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      setForceShowDateRow(true);
      setTimeout(() => {
        const el = document.getElementById('algoliaStartDate');
        if (el) { el.focus(); el.click(); }
      }, 350);
    };
    window.addEventListener('ras:openDatePicker', handler);
    return () => window.removeEventListener('ras:openDatePicker', handler);
  }, []);

  // Collapse the date row back once the calendar closes (user applied or dismissed)
  useEffect(() => {
    if (!focusedDateInput) {
      setForceShowDateRow(false);
    }
  }, [focusedDateInput]);

  // Get location-based placeholder text
  const pKey = !isDesktopLayout ? 'AlgoliaKeywordSearch.placeholderMobile' : 'AlgoliaKeywordSearch.placeholder';
  const dynamicPlaceholder = useLocationBasedPlaceholder(pKey);


  useEffect(() => {
    // When returning from map-only mode, reset sticky state so the observer
    // recalculates from scratch rather than keeping a stale value.
    if (!showOnlySearchInputs) {
      setIsSticky(false);
    }

    const sentinel = stickyTriggerRef.current;
    if (!sentinel?.parentNode) return;

    // Prevent the browser's scroll-anchoring algorithm from adjusting
    // window.scrollY in response to the height changes that occur when the
    // search inputs transition between in-flow and position:fixed — the same
    // pattern used in DesktopSearchBar.
    const parent = sentinel.parentNode;
    parent.style.overflowAnchor = 'none';

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextSticky = !entry.isIntersecting;
        setIsSticky(prev => (prev === nextSticky ? prev : nextSticky));
      },
      // -80px matches the old getBoundingClientRect threshold (offsetTop <= 80).
      { rootMargin: '-80px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(sentinel);

    return () => {
      parent.style.overflowAnchor = '';
      observer.disconnect();
    };
  }, [showOnlySearchInputs]);

  const { keyword: qKeyword, bounds: qBounds, address: qAddress, sort: qSort } =
    queryParams || {};
  // Dates are stored in parent React state (not URL) to prevent Algolia re-queries
  const qStartDate = dateRange?.startDate || null;
  const qEndDate = dateRange?.endDate || null;

  // Mirrors DesktopSearchBar: once pinned, rows that are already filled collapse
  // away and only the still-empty fields stay visible in the sticky bar. When every
  // field is filled the whole bar collapses down to nothing.
  const isDatesEmpty = !qStartDate || !qEndDate;
  const isLocationEmpty = !qAddress;
  const isKeywordEmpty = !qKeyword;
  const hasIncompleteFields = isDatesEmpty || isLocationEmpty || isKeywordEmpty;
  // forceShowDateRow (triggered by "Change Dates") overrides collapse so the date
  // row expands in place without any page scroll.
  const isCollapsing = isSticky && !hasIncompleteFields && !forceShowDateRow;
  const hideDateRow = isSticky && !isDatesEmpty && !isCollapsing && !forceShowDateRow;
  const hideLocationRow = isSticky && !isLocationEmpty && !isCollapsing;
  const hideKeywordRow = isSticky && !isKeywordEmpty && !isCollapsing;

  // Keep a ref so handleRefineKeyword always reads the latest queryParams,
  // even when called from a stale 600ms timer closure in KeywordInput.
  const queryParamsRef = useRef(queryParams);
  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  // When the user's location resolves (auto-detected) but isn't in the URL yet,
  // replace the current URL entry so the share URL includes the city/bounds.
  useEffect(() => {
    const { address: ciAddress, bounds: ciBounds, origin: ciOrigin, city: ciCity } =
      currentUserLocationInfo || {};
    if (!qAddress && ciAddress && ciBounds) {
      const locationParams = {
        address: ciAddress,
        bounds: ciBounds,
        ...(ciOrigin ? { origin: ciOrigin } : {}),
        ...(ciCity ? { city: ciCity } : {}),
      };
      history.replace(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          {},
          { ...(queryParamsRef.current || {}), ...locationParams }
        )
      );
    }
    // Only re-run when the location data itself changes, not on every queryParams change.
  }, [currentUserLocationInfo]);

  const handleRefineLocation = (location) => {
    if (
      location &&
      location.address &&
      location.bounds &&
      (!history.location.state?.fromLandingPage || !location.fromLandingPage)
    ) {
      delete location?.isLandingPage;
      // Reset map exploring state when user manually selects a location
      // This ensures text updates even if query params don't change (e.g., reselecting same location)
      if (onLocationSelection) {
        onLocationSelection();
      }
      const updatedParams = { ...queryParams, ...location };
      return history.push(
        createResourceLocatorString(
          'SearchPage',
          routesConfiguration,
          {},
          updatedParams
        )
      );
    }
    return null;
  };

  const handleRefineKeyword = (keyword = '') => {
    // Use ref so the timer closure in KeywordInput always sees the latest params
    // (e.g. location may have been added to the URL after the timer was set).
    const updatedParams = { ...queryParamsRef.current, keyword: keyword || '' };
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routesConfiguration,
        {},
        updatedParams
      )
    );
  };


  const handleShowRentals = (selectedFilters) => {
    const updatedParams = { ...(queryParams || {}) };

    Object.entries(selectedFilters).forEach(([attribute, values]) => {
      if (attribute === 'sort') {
        if (values) {
          updatedParams['sort'] = values;
        } else {
          delete updatedParams['sort'];
        }
      } else if (attribute === 'priceRange' && Array.isArray(values)) {
        if (values[0] != 0 || values[1] != 10000) {
          updatedParams['priceRange'] = `${values[0] * 100},${values[1] * 100}`; // convert to "min,max"
        } else {
          delete updatedParams['priceRange']
        }
      } else if (typeof values === 'string' || typeof values === 'number') {
        updatedParams[attribute] = values;
      }
    });

    history.push(
      createResourceLocatorString('SearchPage', routesConfiguration, {}, updatedParams)
    );

    if (typeof window !== 'undefined') {
      const userToken = currentUser?.id?.uuid;
      if (userToken) {
        const indexName = getAlgoliaIndexNameForLang(locale || 'en', SUPPORTED_LANGUAGES);
        const filtersPayload = buildFilterPayload(selectedFilters);
        if (filtersPayload.length) {
          loadAlgoliaInsights().then(aa => {
            if (!aa || typeof aa !== 'function') return;
            aa('clickedFilters', {
              userToken,
              eventName: 'Filters Applied',
              index: indexName,
              filters: filtersPayload.slice(0, 20),
            });
            aa('viewedFilters', {
              userToken,
              eventName: 'Filters Viewed',
              index: indexName,
              filters: filtersPayload.slice(0, 20),
            });
          });
        }
      }
    }

    setIsOpen(false);
  };

  const handleFilterClick = useCallback((filterKey, value) => {
    if (typeof window === 'undefined') return;
    const userToken = currentUser?.id?.uuid;
    if (!userToken) return;

    const indexName = getAlgoliaIndexNameForLang(locale || 'en', SUPPORTED_LANGUAGES);
    const filterValue = formatFacetValue(value);

    loadAlgoliaInsights().then(aa => {
      if (!aa || typeof aa !== 'function') return;
      aa('clickedFilters', {
        userToken,
        eventName: 'Filter Clicked',
        index: indexName,
        filters: [`publicData.${filterKey}:${filterValue}`],
      });
    });
  }, [currentUser, locale]);



  const handleClearFilters = () => {
    // Remove all the filters.
    const filteredParams = omitProperties(
      queryParams,
      [...[...filters, ...moreFilters].map(f => f.attribute.split('.')[1]), 'priceRange']
    );


    history.push(
      createResourceLocatorString(
        'SearchPage',
        routesConfiguration,
        {},
        filteredParams
      )
    );
  };


  // Sync pending date state when parent dateRange prop changes
  useEffect(() => {
    setPendingStartDate(qStartDate ? moment(qStartDate, 'YYYY-MM-DD').startOf('day').toDate() : null);
    setPendingEndDate(qEndDate ? moment(qEndDate, 'YYYY-MM-DD').startOf('day').toDate() : null);
  }, [qStartDate, qEndDate]);

  const handleDatesChange = ({ startDate, endDate }) => {
    setPendingStartDate(startDate || null);
    setPendingEndDate(endDate || null);
  };

  const handleApplyDates = () => {
    if (pendingStartDate && pendingEndDate) {
      // Store dates in parent state (NOT in URL) to prevent loadData re-fires,
      // Algolia re-queries, and scroll-to-top on date selection.
      if (onDateRangeChange) {
        onDateRangeChange({
          startDate: moment(pendingStartDate).format('YYYY-MM-DD'),
          endDate: moment(pendingEndDate).format('YYYY-MM-DD'),
        });
      }
      // Becomes the new default everywhere a date picker prefills from
      // util/rentalDefaults.js, even if this range is never booked - a genuine Apply
      // here is a real user gesture, unlike a prefill landing programmatically.
      saveRentalDefaults(
        pickRentalDefaultsFromValues({
          bookingDates: { startDate: pendingStartDate, endDate: pendingEndDate },
        })
      );
    }
    setFocusedDateInput(null);
  };

  const handleClearDates = () => {
    setPendingStartDate(null);
    setPendingEndDate(null);
    if (onDateRangeChange) {
      onDateRangeChange({ startDate: null, endDate: null });
    }
  };

  // Focusing Start Date means "re-pick the whole range", so both inputs are emptied and
  // the calendar stays open on Start Date - same behaviour as the listing page's
  // BookingDatesForm, and as DesktopSearchBar. See useHasUserInteracted for why the
  // gesture gate is needed before a focus event may be treated as a real one.
  const handleDateInputFocus = focusedInput => {
    setFocusedDateInput(focusedInput);
    if (
      focusedInput === START_DATE &&
      hasUserInteractedRef.current &&
      (pendingStartDate || pendingEndDate)
    ) {
      handleClearDates();
    }
  };

  const textAnything = intl.formatMessage({ id: "AlgoliaSearchSection.anything" });

  // Stable callbacks — must be defined unconditionally (Rules of Hooks)
  const memoizedRefineLocation = useCallback(l => handleRefineLocation(l), [queryParams]);
  const memoizedRefineKeyword = useCallback(k => handleRefineKeyword(k), [queryParams]);

  // Calculate margin-bottom based on panelOffset
  // When panelOffset = -200 (default), margin = 0
  // As panelOffset increases towards 0, margin becomes more negative
  const PANEL_MIN_MARGIN = -230;
  const searchInputsMarginBottom = panelOffset !== null
    ? -(panelOffset - PANEL_MIN_MARGIN)
    : undefined;

  return (
    <>
      {/* Title + subtitle — visible on both desktop and mobile */}
      <div
        style={
          showOnlySearchInputs && isDraggingPanel
            ? { position: 'relative', zIndex: 9, marginTop: `${searchInputsMarginBottom > -30 ? -155 : -155 + -(searchInputsMarginBottom + 40)}px` }
            : showOnlySearchInputs && !isDraggingPanel
              ? { display: 'none' }
              : undefined
        }
        className={css.mainFilterWrapper}
      >
        <div>
          <h1 className={css.title}>
            {!neighborhood && !isMapExploring ? (
              <FormattedMessage
                id="AlgoliaSearchSection.titleGlobal"
                values={{
                  keyword: <span className={css.keyword}>{truncateText(qKeyword || textAnything)}</span>,
                }}
              />
            ) : (
              <FormattedMessage
                id="AlgoliaSearchSection.title"
                values={{
                  keyword: <span className={css.keyword}>{truncateText(qKeyword || textAnything)}</span>,
                  neighborhood: (
                    <span className={css.neighborhood}>
                      {isMapExploring
                        ? intl.formatMessage({ id: 'AlgoliaSearchSection.mapArea' }, { defaultMessage: 'the map area' })
                        : truncateText(neighborhood)}
                    </span>
                  ),
                }}
              />
            )}
          </h1>
          <h2 className={css.subtitle}>
            <FormattedMessage id="AlgoliaSearchSection.subtitle" />
          </h2>
        </div>
      </div>

      {/* Mobile search inputs — hidden on desktop (DesktopSearchBar inside leftContent handles desktop) */}
      <div
        ref={searchInputsWrapperRef}
        className={classNames(
          css.searchInputsWrapper,
          isSticky && css.stickyBorderWithBg,
          isCollapsing && css.stickyHidden,
          showOnlySearchInputs && !isDraggingPanel && css.stickyAlways
        )}
        style={
          searchInputsMarginBottom !== undefined
            ? {
              zIndex: showOnlySearchInputs && isDraggingPanel ? 9 : undefined,
              position: showOnlySearchInputs && isDraggingPanel ? 'relative' : undefined,
              background: showOnlySearchInputs && isDraggingPanel ? '#FEF3EE' : undefined,
            }
            : undefined
        }
      >
        <div className={classNames(css.searchInputs)}>
          {/* Date pickers — always mounted; collapsed via CSS once sticky and dates are filled */}
          <div className={classNames(css.datePickers, isSticky && !isLocationEmpty && isDatesEmpty && css.datesEmpty, isSticky && !isLocationEmpty && qStartDate && !qEndDate && css.endDateHighlight, (hideDateRow || isCollapsing) && css.rowCollapsed)}>
            <DateRangeInput
              value={{ startDate: pendingStartDate, endDate: pendingEndDate }}
              onChange={handleDatesChange}
              onFocus={handleDateInputFocus}
              onBlur={() => { }}
              onApply={handleApplyDates}
              onRemove={handleClearDates}
              isDaily={true}
              isDayBlocked={() => () => false}
              isOutsideRange={() => day => moment(day).isBefore(moment(), 'day')}
              minimumNights={0}
              focusedInput={focusedDateInput}
              startDateId="algoliaStartDate"
              endDateId="algoliaEndDate"
              startDatePlaceholderText={intl.formatMessage({ id: 'AlgoliaSearchSection.startDatePlaceholder' })}
              endDatePlaceholderText={intl.formatMessage({ id: 'AlgoliaSearchSection.endDatePlaceholder' })}
              firstDayOfWeek={config?.localization?.firstDayOfWeek || 0}
              hideKeyboardShortcutsPanel={true}
              showDefaultInputIcon={false}
              noBorder={true}
              block={true}
              withPortal={false}
              useMobileMargins={false}
              name="algoliaDateRange"
            />
          </div>

          {/* Location — always mounted; collapsed via CSS once sticky and location is filled */}
          <div className={classNames(css.locationRowWrapper, (hideLocationRow || isCollapsing) && css.rowCollapsed)}>
            <AlgoliaLocationSearch
              attribute={'publicData.location.address'}
              className={isLocationEmpty ? css.locationHighlight : undefined}
              defaultLocation={currentUserLocationInfo}
              isMapExploring={isMapExploring}
              onRefineLocation={memoizedRefineLocation}
              initialValues={{ bounds: qBounds, address: qAddress }}
              showInlineError={false}
            />
          </div>

          {/* Keyword + inline filter icon — always mounted; collapsed via CSS once sticky and keyword is filled */}
          <div className={classNames(css.keywordRow, !isLocationEmpty && !isDatesEmpty && isKeywordEmpty && css.keywordHighlight, (hideKeywordRow || isCollapsing) && css.rowCollapsed)}>
            <AlgoliaKeywordSearch
              onSearchChange={memoizedRefineKeyword}
              initialValue={{ keyword: qKeyword }}
              history={history}
              currentUserCurrency={currentUserCurrency}
              currentUser={currentUser}
              exchangeRates={exchangeRates}
              routes={routesConfiguration}
              config={config}
              intl={intl}
              clearKeywordsClass={css.clearKeywords}
              autoFocus={true}
              selectedBounds={selectedBounds}
              onActivateListing={onActivateListing}
              searchInputText={dynamicPlaceholder}
              algoliaListings={algoliaListings}
              algoliaStores={algoliaStores}
              algoliaQueryInProgress={algoliaQueryInProgress}
              showMap={showMap}
              locale={locale}
            />
            <button
              type="button"
              className={css.filterIconInline}
              onClick={Object.keys(selectedMoreFilters || {}).length > 0 ? handleClearFilters : () => setIsOpen(true)}
            >
              <IconCollection name={Object.keys(selectedMoreFilters || {}).length > 0 ? 'clear-filters' : 'filter_icon'} />
            </button>
          </div>
        </div>
      </div>
      {/* Stable 1px sentinel for IntersectionObserver — placed before the placeholder
          so its position is never affected by the stickyBorderHeight height change. */}
      {!showOnlySearchInputs && (
        <div
          ref={stickyTriggerRef}
          aria-hidden="true"
          style={{ height: '1px', marginTop: '-1px', overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }}
        />
      )}
      {/* Placeholder fills the space vacated by the search inputs when they go position:fixed */}
      {!showOnlySearchInputs && (
        <div ref={filterContainerRef} className={classNames(isSticky && css.stickyBorderHeight)} />
      )}

      {/* Mobile filter panel (shown when filter icon is tapped) */}
      <div className={css.filterMobileWrapper}>
        {isOpen && (
          <AlgoliaFilters
            facets={facets}
            manageDisableScrolling={manageDisableScrolling}
            handleShowRentals={handleShowRentals}
            setIsOpen={setIsOpen}
            isOpen={isOpen}
            selectedFilters={selectedMoreFilters}
            showResetButton={Object.keys(selectedMoreFilters || {}).length > 0}
            onResetFilters={handleClearFilters}
            onFilterClick={handleFilterClick}
            sortOptions={sortOptions}
            currentSort={qSort || defaultSort}
          />
        )}
      </div>
    </>
  );
};

export default SearchSection;
